import { Key, useEffect, useState } from 'react';

import { Flex, Select, SelectItem, TextField } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

const timeOptions = [
  { value: 1, label: 'common.minutes', limit: 60 },
  { value: 60, label: 'common.hours', limit: 24 },
  { value: 60 * 24, label: 'common.days', limit: 365 },
  { value: 60 * 24 * 7, label: 'common.weeks', limit: 52 },
  { value: 60 * 24 * 30, label: 'common.months', limit: 12 },
];

const findOpt = (match: string) =>
  timeOptions.find((opt) => opt.value === parseInt(match, 10)) ||
  timeOptions[2];

interface Props {
  isDisabled: boolean;
  onChange: (value: [string, string]) => void;
  value: string | string[];
}

const TimeInput = ({ isDisabled, onChange, value }: Props) => {
  const [digit, setDigit] = useState(value[0] || '');
  const [unit, setUnit] = useState(findOpt(value[1]));
  const { t } = useTranslation();

  function handleChangeInput(value: string) {
    const next = parseInt(value, 10);
    if (!value || (/^\d+$/.test(value) && next > 0 && next <= unit.limit)) {
      onChange([value, String(unit.value)]);
    }
  }

  function handleChangeSelect(minutes: Key) {
    const option = findOpt(minutes as string);
    const limit = option?.limit || 365;
    const value = option?.value || 1440;
    const next = parseInt(digit, 10) > limit ? limit : digit;
    onChange([String(next), String(value)]);
  }

  useEffect(() => {
    setDigit(value[0] || '');
    setUnit(findOpt(value[1]));
  }, [value]);

  return (
    <Flex css={{ gap: '0.5rem' }}>
      <TextField
        aria-label={t('common.inputNumberOf', {
          unitName: unit.label,
        })}
        isDisabled={isDisabled}
        value={digit}
        onChange={handleChangeInput}
        placeholder={`1 - ${unit.limit}`}
      />
      <Select
        aria-label={t('common.selectTimeUnit')}
        isDisabled={isDisabled}
        onSelectionChange={handleChangeSelect}
        selectedKey={String(unit.value)}
      >
        {timeOptions.map((opt) => (
          <SelectItem key={opt.value} aria-label={t(opt.label)}>
            {t(opt.label)}
          </SelectItem>
        ))}
      </Select>
    </Flex>
  );
};

export default TimeInput;
